import React, { useState, useEffect, useCallback } from "react";
import DashboardHeader from "../../components/auth/DashboardHeader";
import Sidebar from "../../components/auth/Sidebar";
import { Link, useParams, useNavigate } from "react-router-dom";
import { errorSwal, formatNumber } from "../../helper/helper";
import { Loader } from "../../components/Loader";
import { toast } from "react-toastify";
import API from "../../helper/AxiosInterceptor";
import copy from "copy-to-clipboard";
import WalletWhitelistTab from "../../components/auth/WalletWhitelistTab";

const AppsDetail = () => {
  const [appInfo, setAppInfo] = useState("");
  const { appId } = useParams();
  const [tokens, setTokens] = useState([]);
  const [updatedTokens, setUpdatedTokens] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [priceIn, setPriceIn] = useState("USD");
  const [price, setPrice] = useState({
    BTC: 0,
    ETH: 0,
    WBNB: 0,
    BNB: 0,
    USDT: 0,
    USDC: 0,
    AVAX: 0,
    MATIC: 0,
    TRX: 0,
  });
  const currencyList = [
    { name: "USD", value: "USD" },
    { name: "TRY", value: "TRY" },
    { name: "AUD", value: "AUD" },
  ];
  const [publicKey, setPublicKey] = useState("");
  const [privateKey, setPrivateKey] = useState("");

  const [totalFiatToUsd, setTotalFiatToUsd] = useState(0);

  const [activeTab, setActiveTab] = useState("assets");

  function getPrice(token) {
    switch (token.toUpperCase()) {
      case "BTC":
        return price.BTC;
      case "ETH":
        return price.ETH;
      case "BNB":
      case "WBNB":
        return price.BNB;
      case "USDT":
        return price.USDT;
      case "USDC":
        return price.USDC;
      case "AVAX":
        return price.AVAX;
      case "MATIC":
        return price.MATIC;
      case "TRX":
        return price.TRX;
      default:
        console.log("Unknown token: " + token);
        return 0;
    }
  }

  console.log("RR price : ", price);
  console.log("tokens : ", tokens);

  const fetchAppDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await API.get(
        `${process.env.REACT_APP_API_URL}/apps/getById`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: { appId },
        }
      );
      if (response?.data) {
        console.log("response of app", response?.data);
        setAppInfo(response?.data?.data);
        setTotalFiatToUsd(response?.data?.totalFiatToUsd);
      }
    } catch (error) {
      console.error("Error fetching app details:", error);
    } finally {
      setIsLoading(false);
    }
  }, [appId]);

  const getCryptoPrice = useCallback(async () => {
    try {
      const currency = priceIn.toLowerCase();
      // Ensure we fetch all required symbols
      const symbols = "BTC,ETH,BNB,USDT,USDC,AVAX,MATIC,TRX";

      const response = await API.post(`${process.env.REACT_APP_API_URL}/common/price`, {
        currency: currency.toUpperCase(),
        symbol: symbols
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response?.data?.success && response?.data?.data) {
        const prices = response.data.data;
        setPrice({
          BTC: prices.BTC,
          ETH: prices.ETH,
          BNB: prices.BNB,
          WBNB: prices.BNB, // WBNB price same as BNB
          USDT: prices.USDT,
          USDC: prices.USDC,
          AVAX: prices.AVAX,
          MATIC: prices.MATIC,
          TRX: prices.TRX,
          fiatRate: prices.USDT, // Assuming fiatRate was tether
        });
      }
    } catch (error) {
      console.error("Error fetching crypto prices:", error);
    }
  }, [priceIn]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 2000); // 1 second debounce delay

    return () => {
      clearTimeout(handler); // Clear the timeout if searchQuery changes before 1 second
    };
  }, [searchQuery]);

  const fetchTokens = useCallback(async () => {
    setIsLoading(true);
    await getCryptoPrice();
    try {
      const response = await API.get(
        `${process.env.REACT_APP_API_URL}/token/list`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            pageNo: currentPage,
            limitVal: 12,
            search: searchQuery,
          },
        }
      );
      const tokens = response?.data?.data;
      setTokens(tokens);
      console.log("tokens", tokens);
      setCurrentPage(response?.data?.currentPage);
      setTotalPages(response?.data?.totalPages);
    } catch (error) {
      console.error("Error fetching tokens:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearchQuery, getCryptoPrice]);

  // Fetch balances from the backend API (replaces direct blockchain calls)
  const fetchBalancesFromBackend = useCallback(async () => {
    if (!appId || tokens.length === 0) return;
    setIsLoading(true);
    try {
      const response = await API.get(
        `${process.env.REACT_APP_API_URL}/user-withdrawal/merchant/balance`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: { appId },
        }
      );

      if (response?.data?.success && response?.data?.data?.balances) {
        const backendBalances = response.data.data.balances;

        // Merge backend balances with token list
        const merged = tokens.map((item) => {
          // Match by symbol + chainId for accuracy
          const match = backendBalances.find(
            (b) =>
              b.symbol === item.symbol &&
              b.chainId === item.chainId
          );
          const balance = match ? parseFloat(match.balance) || 0 : 0;
          const _token = (
            item?.code.split(".")[0] ?? item?.code
          ).toUpperCase();
          const tokenPrice = getPrice(_token);
          return {
            ...item,
            balance: balance,
            usd: tokenPrice * balance,
          };
        });

        console.log("Merged tokens with backend balances:", merged);
        setUpdatedTokens(merged);
      }
    } catch (e) {
      console.error("Error fetching balances from backend:", e);
      // Fallback: show tokens with 0 balance
      setUpdatedTokens(tokens.map((t) => ({ ...t, balance: 0, usd: 0 })));
    } finally {
      setIsLoading(false);
    }
  }, [tokens, appId, price]);

  useEffect(() => {
    fetchAppDetails();
  }, [fetchAppDetails]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  useEffect(() => {
    fetchBalancesFromBackend();
  }, [tokens, fetchBalancesFromBackend]);

  const handlePageChange = (pageNo) => {
    if (pageNo > 0 && pageNo <= totalPages) {
      setCurrentPage(pageNo);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const fetchTokensForTokenExistence = async (tokenId) => {
    // Fetch the tokens
    const response = await API.get(
      `${process.env.REACT_APP_API_URL}/token/list`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          pageNo: 1,
          limitVal: 100,
          search: "",
        },
      }
    );

    const tokens = response?.data?.data;

    // Check if the token exists
    const tokenExists = tokens.some((token) => token._id === tokenId);

    return tokenExists;
  };

  const handleSendNavigation = async (tokenId) => {
    try {
      const isTokenExists = await fetchTokensForTokenExistence(tokenId);
      if (isTokenExists) {
        // Navigate to the appropriate route if the token exists
        navigate(`/apps-send-withdraw/${appId}`, { state: { tokenId } });
      } else {
        // Show an error if the token does not exist
        errorSwal(
          "Token Not Found!",
          `Unfortunately, this token has been removed.`
        );
      }
      await fetchTokens();
    } catch (error) {
      console.error("Error fetching tokens:", error);
      errorSwal("Error", "Unable to fetch tokens. Please try again later.");
    }
  };

  const handleDepositNavigation = async (tokenId, tokenNetwork) => {
    try {
      // alert(`Token ${tokenId}`);
      setIsLoading(true);
      const isTokenExists = await fetchTokensForTokenExistence(tokenId);
      if (isTokenExists) {
        // Navigate to the appropriate route if the token exists
        navigate(`/apps-deposit-receive/${appId}`, {
          state: { tokenId, tokenNetwork },
        });
      } else {
        // Show an error if the token does not exist
        errorSwal(
          "Token Not Found!",
          `Unfortunately, this token has been removed.`
        );
      }
      await fetchTokens();
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching tokens:", error);
      errorSwal("Error", "Unable to fetch tokens. Please try again later.");
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchApiKeys = async (appId) => {
    try {
      const response = await API.get(
        `${process.env.REACT_APP_API_URL}/apps/keys`,

        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            appId: appId,
          },
        }
      );

      if (response?.data) {
        setPublicKey(response?.data?.publicKey);
        setPrivateKey(response?.data?.privateKey);
      }
    } catch (error) {
      console.error("Error fetching API keys:", error);
    }
  };

  const handleApiKeyClick = (appId) => {
    fetchApiKeys(appId);
  };

  const handleCopyClick = (text) => {
    copy(text);
    // navigator.clipboard
    //   .writeText(text)
    //   .then(() => {
    //     toast.success("Copied to clipboard!");
    //   })
    //   .catch((err) => {
    //     console.error("Failed to copy: ", err);
    //   });
  };

  console.log("updatedTokens : ", updatedTokens);
  console.log("appInfo : ", appInfo);

  return (
    <>
      <DashboardHeader appId={appInfo?._id} />
      <div className="site-bg">
        <div className="dashborad-content-bg">
          <aside className="sidebar">
            <Sidebar />
          </aside>
          <div className="dashborad-warper" style={{ position: "relative" }}>
            {isLoading && <Loader inline={true} />}
            <div className="transactions-list">
              <div className="dashborad-head">
                <div className="breadcrumb-menu">
                  <ul>
                    <li className="active">
                      <Link to="/apps">Apps</Link>
                    </li>
                    <li>
                      <a>&rarr; {appInfo?.name} Details</a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="white-bg padd20 api-key-payment-box">
                <div className="api-key-payment-left">
                  <h3>{appInfo?.name}</h3>
                  {totalFiatToUsd > 0 && (
                    <p>
                      Total Fiat Balance: {formatNumber(totalFiatToUsd, 4)} USD
                    </p>
                  )}
                  <div className="table-action-btn api-key-btn">
                    <a
                      href=""
                      data-bs-toggle="modal"
                      data-bs-target="#exampleModal2"
                      onClick={() => handleApiKeyClick(appInfo?._id)}
                    >
                      <i className="ti ti-key" />
                      <span>API Key</span>
                    </a>
                  </div>
                </div>
                <div className="api-key-payment-right">
                  <a
                    className="btn"
                    onClick={() => {
                      if (appInfo?._id) {
                        navigate(`/apps-payment-links/${appId}`);
                      }
                    }}
                  >
                    <i className="ti ti-arrow-down-left" />
                    <span>Payment Link History</span>
                  </a>
                  <a
                    className="btn"
                    onClick={() => {
                      if (appInfo?._id) {
                        navigate(`/apps-wallet-transactions/${appId}`);
                      }
                    }}
                  >
                    <i className="ti ti-arrow-up-right" />
                    <span>Wallet History</span>
                  </a>
                </div>
              </div>

              <div className="mt-4 mb-3" style={{ borderBottom: "2px solid #eef0f4", display: "flex", gap: "10px" }}>
                <button
                  style={{
                    background: "none",
                    border: "none",
                    borderBottom: activeTab === 'assets' ? "3px solid #E9C02F" : "3px solid transparent",
                    padding: "10px 20px",
                    fontWeight: activeTab === 'assets' ? "700" : "500",
                    fontSize: "16px",
                    color: activeTab === 'assets' ? "#1f2937" : "#6b7280",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    marginBottom: "-2px",
                    display: "flex",
                    alignItems: "center"
                  }}
                  onClick={() => setActiveTab('assets')}
                  onMouseEnter={(e) => {
                    if (activeTab !== 'assets') e.currentTarget.style.color = "#1f2937";
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== 'assets') e.currentTarget.style.color = "#6b7280";
                  }}
                >
                  <i className="ti ti-wallet" style={{ fontSize: '20px', marginRight: '8px' }}></i>
                  Asset List
                </button>
                <button
                  style={{
                    background: "none",
                    border: "none",
                    borderBottom: activeTab === 'whitelist' ? "3px solid #E9C02F" : "3px solid transparent",
                    padding: "10px 20px",
                    fontWeight: activeTab === 'whitelist' ? "700" : "500",
                    fontSize: "16px",
                    color: activeTab === 'whitelist' ? "#1f2937" : "#6b7280",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    marginBottom: "-2px",
                    display: "flex",
                    alignItems: "center"
                  }}
                  onClick={() => setActiveTab('whitelist')}
                  onMouseEnter={(e) => {
                    if (activeTab !== 'whitelist') e.currentTarget.style.color = "#1f2937";
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== 'whitelist') e.currentTarget.style.color = "#6b7280";
                  }}
                >
                  <i className="ti ti-list-check" style={{ fontSize: '20px', marginRight: '8px' }}></i>
                  Wallet Whitelist
                </button>
              </div>

              {activeTab === 'assets' && (
                <div className="white-bg my-apps-content padd20">
                  <div className="search-filter-box">
                    <div className="dashborad-search-box">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Blockchain Network"
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                    </div>
                  </div>
                  <div className="table-box">
                    <table className="table">
                      <tbody>
                        <tr>
                          <th>Coin Name</th>
                          <th>Blockchain Network</th>
                          <th>Code</th>
                          <th>Min Deposit</th>
                          <th>Balance</th>
                          <th>
                            <div className="value-box">
                              <span>Value</span>
                              <div className="usd-select">
                                <select
                                  className="form-control"
                                  onChange={(event) => {
                                    console.log(
                                      "Selected value is : ",
                                      event.target.value
                                    );
                                    setPriceIn(event.target.value);
                                  }}
                                >
                                  {currencyList &&
                                    currencyList.map((currency) => (
                                      <option
                                        key={currency.value}
                                        value={currency.value}
                                      >
                                        {currency.name}
                                      </option>
                                    ))}
                                </select>
                              </div>
                            </div>
                          </th>
                          <th>Commands</th>
                        </tr>

                        {totalFiatToUsd > 0 && (
                          <tr
                            style={{ backgroundColor: "rgba(228, 31, 57, 0.25)" }}
                          >
                            <td>USD</td>
                            <td>-</td>
                            <td>USD</td>
                            <td>{formatNumber(totalFiatToUsd, 4)}</td>

                            <td>
                              {priceIn === "USD"
                                ? "$"
                                : priceIn === "TRY"
                                  ? "₺"
                                  : "AU$"}{" "}
                              {/* {formatNumber(totalFiatToUsd?.toPrecision(10), 5)} */}
                              {formatNumber(
                                (totalFiatToUsd * price?.fiatRate)?.toPrecision(
                                  10
                                ),
                                5
                              )}
                            </td>

                            <td>
                              <div className="option-box">
                                <div className="dropdown">
                                  <button
                                    className="dropdown-toggle"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    Options
                                  </button>
                                  <ul className="dropdown-menu">
                                    <li>
                                      <button
                                        onClick={() =>
                                          navigate(
                                            `/apps-send-withdraw-fiat/${appInfo?._id}`
                                          )
                                        }
                                        className="dropdown-item"
                                      >
                                        Send/Withdraw
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {updatedTokens.map((val, key) => (
                          <tr key={key}>
                            <td>{val?.symbol}</td>
                            <td>{val?.network}</td>
                            <td>{val?.code}</td>
                            <td>{val?.minDeposit || 0}</td>
                            <td>{formatNumber(Number(val?.balance), 8)}</td>
                            {/* <td>{formatNumber(val?.usd, 8)}</td> */}
                            <td>
                              {priceIn === "USD"
                                ? "$"
                                : priceIn === "TRY"
                                  ? "₺"
                                  : "AU$"}{" "}
                              {formatNumber(val?.usd?.toPrecision(10), 5)}
                            </td>

                            <td>
                              <div className="option-box">
                                <div className="dropdown">
                                  <button
                                    className="dropdown-toggle"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    Options
                                  </button>
                                  <ul className="dropdown-menu">
                                    <li>
                                      <button
                                        onClick={() =>
                                          handleDepositNavigation(
                                            val._id,
                                            val.network
                                          )
                                        }
                                        className="dropdown-item"
                                      >
                                        Deposit/Receive
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        onClick={() =>
                                          handleSendNavigation(val._id)
                                        }
                                        className="dropdown-item"
                                      >
                                        Send/Withdraw
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        onClick={() => {
                                          if (appInfo?._id) {
                                            navigate(
                                              `/apps-payment-links/${appId}`
                                            );
                                          }
                                        }}
                                        className="dropdown-item"
                                      >
                                        Payment Link History
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        onClick={() => {
                                          if (appInfo?._id) {
                                            navigate(
                                              `/apps-wallet-transactions/${appId}`
                                            );
                                          }
                                        }}
                                        className="dropdown-item"
                                      >
                                        Wallet History
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="pagination-box">
                    <ul>
                      <li>
                        <a
                          href=""
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage - 1);
                          }}
                          disabled={currentPage === 1}
                        >
                          <img src="images/left-arrow.png" alt="arrow" />
                        </a>
                      </li>
                      {[...Array(totalPages).keys()].map((pageNo) => (
                        <li
                          key={pageNo + 1}
                          className={currentPage === pageNo + 1 ? "active" : ""}
                        >
                          <a
                            href=""
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(pageNo + 1);
                            }}
                          >
                            {pageNo + 1}
                          </a>
                        </li>
                      ))}
                      <li>
                        <a
                          href=""
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage + 1);
                          }}
                          disabled={currentPage === totalPages}
                        >
                          <img src="images/right-arrow.png" alt="arrow" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'whitelist' && (
                appInfo?._id ? <WalletWhitelistTab appId={appInfo._id} /> : null
              )}

            </div>
          </div>
        </div>
      </div>

      {/* API Key */}
      <div
        className="modal fade"
        id="exampleModal2"
        tabIndex={-1}
        aria-labelledby="exampleModalLabel2"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalLabel1">
                API Key
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body api-key-modal">
              <div className="form-group app-id-box">
                <label>Api Key</label>
                <div className="form-group-inner">
                  <input
                    type="text"
                    className="form-control"
                    value={`${publicKey.slice(0, 8)}*****************`}
                    readOnly
                  />
                  <button
                    className="copy-code-btn"
                    onClick={() => {
                      handleCopyClick(publicKey)
                      toast.success("Copied!")
                    }}
                  >
                    <i className="ti ti-copy" />
                  </button>
                </div>
              </div>
              <div className="form-group app-id-box">
                <label>Secret Key</label>
                <div className="form-group-inner">
                  <input
                    type="text"
                    className="form-control"
                    value={`${privateKey.slice(0, 8)}*****************`}
                    readOnly
                  />
                  <button
                    className="copy-code-btn"
                    onClick={() => {
                      handleCopyClick(privateKey)
                      toast.success("Copied!")
                    }}

                  >
                    <i className="ti ti-copy" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppsDetail;
